'use client';
import LinkWrapper from '@/components/link-wrapper/link-wrapper';
import { isBrowser } from '@/helpers/is-browser';
import { useCalculateSlug } from '@/hooks/use-calculate-slug';
import { IContentfulImage } from '@/interfaces/contentful-image.interface';
import {
  IMeetingPlaceLinkToAnyPage,
  MeetingPlaceLinkToAnyPageFragment,
} from '@/lib/fragments/meetingPlaceLinkToAnyPage';
import { useIsMPLivliFamily } from '@/providers/ConfigProvider';
import { useUser } from '@/providers/UserProvider';
import { gql } from '@apollo/client';
import Image from 'next/image';
import React from 'react';

type TextAndImageCardColor = 'Blue' | 'Green' | 'Grey' | 'Red';

export interface TextAndImageCardBlockProps {
  title: string;
  cardText: string;
  cardLink?: IMeetingPlaceLinkToAnyPage;
  mediaField: {
    jsonBynderAsset: {
      originalUrl: string;
      thumbnail: string;
    };
    altText: string;
  };
  cardTextOverlay: boolean;
  cardColor: TextAndImageCardColor;
  externalLink?: string;
  isMobileMenuCard?: boolean;
  isHomePage?: boolean;
}

const TextAndImageCardBlock = ({
  title,
  cardText,
  cardLink,
  mediaField,
  cardTextOverlay,
  cardColor,
  externalLink,
  isMobileMenuCard = false,
  isHomePage = false,
}: TextAndImageCardBlockProps) => {
  const { user } = useUser();
  const isLivliFamily = useIsMPLivliFamily();
  const internalHref = useCalculateSlug(cardLink ?? '');
  const cardLinkHref = cardLink?.linkHref || internalHref;

  const sendGtmEvent = (href: string) => {
    if (isHomePage && isBrowser && dataLayer) {
      let clickUrl = href;
      try {
        clickUrl = new URL(href, window.location.origin).href;
      } catch {
        // Malformed CMS URL: report it as authored.
      }
      dataLayer.push({
        event: 'homepage_tile_click',
        tile_name: title,
        click_url: clickUrl,
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      });
    }
  };

  const cardImage = mediaField?.jsonBynderAsset?.originalUrl;
  const commonBackgroundColorClasses =
    'relative bg-cover aspect-1/0.7 transition-all duration-200 ease-in-out lg:hover:brightness-90';
  const cardBackgroundColorClass = (color: TextAndImageCardColor) => {
    switch (color) {
      case 'Blue':
        return `${commonBackgroundColorClasses} bg-brand-blue-1 w-full h-[11rem] text-brand-blue-3 bg-dynamicStrokeBlue`;
      case 'Grey':
        return `${commonBackgroundColorClasses} bg-background-primary-light w-full h-[11rem] text-brand-grey-4 bg-dynamicStrokeGreyDark`;
      case 'Green':
        return `${commonBackgroundColorClasses} bg-brand-green-1 w-full text-brand-green-3 bg-dynamicStrokeGreen`;
      case 'Red':
        return `${commonBackgroundColorClasses} bg-brand-red-1 w-full text-brand-red-3 bg-dynamicStrokeRed`;
      default:
        return '';
    }
  };

  const linkCss = `group relative mb-5 inline-block transform-gpu break-inside-avoid overflow-hidden rounded-xl lg:mb-5 ${
    cardBackgroundColorClass(cardColor)
      ? cardBackgroundColorClass(cardColor)
      : 'absolute inset-0 transition-all duration-200 ease-in-out lg:group-hover:bg-transparency-backdrop text-brand-white'
  }`;

  const text = (
    <div
      className={` ${
        !cardBackgroundColorClass(cardColor) && cardTextOverlay
          ? 'absolute inset-0 bg-gradient-to-b from-transparent to-transparency-gradient-to'
          : 'absolute inset-0 '
      }
${
  cardImage
    ? 'transition-all duration-200 ease-in-out lg:group-hover:bg-transparency-backdrop'
    : ''
}`}
    >
      <div
        className={`absolute bottom-0 left-0 right-0 transform-gpu px-5 ${
          isMobileMenuCard ? 'pb-5 ' : 'pb-10 '
        } ${
          isMobileMenuCard && !cardImage ? 'top-0 flex items-center' : ' '
        }  pt-5`}
      >
        <h2
          className={`font-title ${
            isMobileMenuCard ? 'text-headingxs ' : 'text-headingm '
          } transition-all duration-200 ease-in-out lg:text-headingm ${
            cardImage && 'lg:group-hover:text-brand-yellow-1'
          }`}
        >
          {title}
        </h2>
        <p
          className={`text-bodym ${
            (!cardImage || isMobileMenuCard) &&
            'hidden max-h-0 overflow-hidden opacity-0 transition-all duration-200 ease-in-out lg:block lg:group-hover:max-h-20 lg:group-hover:opacity-100'
          }`}
        >
          {cardText}
        </p>
      </div>
    </div>
  );

  const image = cardImage ? (
    <img
      src={cardImage + '?fm=avif&q=50'}
      alt={mediaField?.altText || 'alt text not found'}
      className="object-cover block w-full"
    />
  ) : // <Image
  //   src={cardImage}
  //   alt=""
  //   className="object-cover block w-full"
  //   // fill
  //   // sizes="(max-width: 768px) 100vw, (max-width: 1200px) 864px, 1200px"
  //   height={1600}
  //   width={1200}
  //   placeholder="blur"
  //   priority
  //   blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAANAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWEREiMxUf/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEQMhkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT0s3kE64Z4T//2Q=="
  //   quality={60}
  // />
  null;

  const Links =
    cardLink && !externalLink ? (
      <LinkWrapper
        data={cardLink}
        className={linkCss}
        onClick={() => sendGtmEvent(cardLinkHref)}
      >
        {image}
        {text}
      </LinkWrapper>
    ) : (
      !cardLink &&
      externalLink && (
        <a
          target={'_blank'}
          rel={'noopener noreferrer'}
          href={externalLink}
          className={linkCss}
          onClick={() => sendGtmEvent(externalLink)}
        >
          {cardImage ? image : null}
          {text}
        </a>
      )
    );

  return <>{Links}</>;
};

export default TextAndImageCardBlock;

